/**
 * Custom hook for managing dynamic properties visibility and values.
 * Extracted from FarmDetailsModal.tsx.
 */

import { useMemo } from "react";
import { DynamicPropertyTypeDto, OrganizationDynamicPropertyDto } from "../../../api/types/dtos/dynamicPropertyTypes";

interface UseDynamicPropertiesProps {
  organizationDynamicProperties?: {
    data: OrganizationDynamicPropertyDto[];
  };
  dynamicPropertyTypes?: {
    data: DynamicPropertyTypeDto[];
  };
}

interface DynamicProperties {
  title: string;
  challengesVisible: boolean;
  attachmentsVisible: boolean;
  pdfHeaderTitle: string;
  pdfHeaderSubtitle: string;
  pdfChallengesVisible: boolean;
  trainingsVisible: boolean;
  pdfTrainingsVisible: boolean;
  pdfAttachmentsVisible: boolean;
}

/**
 * Helper to get string property value with fallbacks
 */
function getStringProperty(
  organizationDynamicProperties: OrganizationDynamicPropertyDto[] | undefined,
  dynamicPropertyTypes: DynamicPropertyTypeDto[] | undefined,
  propertyTypeName: string,
  fallback: string = "",
): string {
  // First, try to find the property in organization dynamic properties
  if (organizationDynamicProperties) {
    const property = organizationDynamicProperties.find(
      (prop) => prop.propertyTypeName === propertyTypeName,
    );

    if (property?.value) {
      return property.value;
    }
  }

  // Fallback: get default value from dynamic property types cache
  if (dynamicPropertyTypes) {
    const propertyType = dynamicPropertyTypes.find(
      (type) => type.name === propertyTypeName,
    );

    if (propertyType?.defaultValue && propertyType.defaultValue.trim() !== "") {
      return propertyType.defaultValue;
    }
  }

  return fallback;
}

/**
 * Helper to get boolean property value with fallbacks
 */
function getBooleanProperty(
  organizationDynamicProperties: OrganizationDynamicPropertyDto[] | undefined,
  dynamicPropertyTypes: DynamicPropertyTypeDto[] | undefined,
  propertyTypeName: string,
  fallback: boolean = false,
): boolean {
  // First, try to find the property in organization dynamic properties
  if (organizationDynamicProperties) {
    const property = organizationDynamicProperties.find(
      (prop) => prop.propertyTypeName?.toLowerCase() === propertyTypeName.toLowerCase(),
    );

    if (property?.value) {
      // Convert string '0'/'1' to boolean (nuevo formato)
      return property.value === "1";
    }
  }

  // Fallback: get default value from dynamic property types cache
  if (dynamicPropertyTypes) {
    const propertyType = dynamicPropertyTypes.find(
      (type) => type.name?.toLowerCase() === propertyTypeName.toLowerCase(),
    );

    if (propertyType?.defaultValue) {
      // Convert string '0'/'1' to boolean
      return propertyType.defaultValue === "1";
    }
  }

  return fallback;
}

/**
 * Hook for managing dynamic properties
 */
export function useDynamicProperties({
  organizationDynamicProperties,
  dynamicPropertyTypes,
}: UseDynamicPropertiesProps): DynamicProperties {
  return useMemo(() => {
    const data = organizationDynamicProperties?.data;
    const types = dynamicPropertyTypes?.data;

    return {
      title: getStringProperty(data, types, "cv_section_title", "Detalles de la Finca"),
      challengesVisible: getBooleanProperty(data, types, "cv_section_challenges", false),
      attachmentsVisible: getBooleanProperty(data, types, "cv_section_attachments", false),
      pdfHeaderTitle: getStringProperty(data, types, "pdf_section_header_title", ""),
      pdfHeaderSubtitle: getStringProperty(data, types, "pdf_section_header_subtitle", "HOJA DE VIDA"),
      pdfChallengesVisible: getBooleanProperty(data, types, "pdf_section_challenges", true),
      trainingsVisible: getBooleanProperty(data, types, "cv_section_trainings", false),
      pdfTrainingsVisible: getBooleanProperty(data, types, "pdf_section_trainings", false),
      pdfAttachmentsVisible: getBooleanProperty(data, types, "pdf_section_attachments", true),
    };
  }, [organizationDynamicProperties, dynamicPropertyTypes]);
}
